/**
 * `ChoiceBoard` builtin surface — pick one item from choices grouped into
 * columns (time slots by day, sizes by type, variants by group). Thin
 * binding: `columns[]` records → generic `ChoiceColumn`s; a column is
 * highlighted when it holds the current selection. Picking stages the choice for the main composer.
 */
import { useContext, type FC } from 'react';
import { A2uiSurfaceContext } from '@/app/lib/a2ui/surface-context.ts';
import type { A2uiNodeViewProps } from '@/app/lib/a2ui/catalog.tsx';
import { isRecord } from '@/app/lib/util/type-guards.ts';
import { SurfaceHeader } from '@/app/lib/a2ui/blocks/SurfaceHeader.tsx';
import {
  ChoiceBoard as ChoiceBoardBlock,
  type ChoiceColumn,
  type ChoiceColumnItem,
} from '@/app/lib/a2ui/blocks/ChoiceBoard.tsx';
import { BlockSkeleton } from '@/app/lib/a2ui/blocks/BlockSkeleton.tsx';
import { arr, optStr, str } from '@/app/lib/a2ui/props.ts';
import { useComposerDraft } from '../../stage/ComposerDraftContext.tsx';

interface ChoiceColumnRecord {
  heading: string;
  caption?: string;
  items: ChoiceColumnItem[];
}

function readItem(raw: unknown): ChoiceColumnItem | null {
  if (!isRecord(raw)) {
    return null;
  }
  const id = str(raw.id);
  const label = str(raw.label);
  if (!id || !label) {
    return null;
  }
  return {
    id,
    label,
    caption: optStr(raw.caption),
    disabled: raw.disabled === true,
  };
}

function readColumn(raw: unknown): ChoiceColumnRecord | null {
  if (!isRecord(raw)) {
    return null;
  }
  const heading = str(raw.heading);
  if (!heading) {
    return null;
  }
  const items = arr(raw.items)
    .map(readItem)
    .filter((item): item is ChoiceColumnItem => item !== null);
  return { heading, caption: optStr(raw.caption), items };
}

export const ChoiceBoardSurface: FC<A2uiNodeViewProps> = ({ node }) => {
  const api = useContext(A2uiSurfaceContext);
  const draft = useComposerDraft();
  const columns = arr(node.props.columns)
    .map(readColumn)
    .filter((column): column is ChoiceColumnRecord => column !== null);

  if (!Array.isArray(node.props.columns) || columns.length === 0) {
    return <BlockSkeleton variant="board" />;
  }

  const stagedSelection = draft?.items.find(
    (item) => item.surfaceId === api?.surfaceId && item.id === node.id,
  )?.context.selection;
  const selectedId = isRecord(stagedSelection)
    ? optStr(stagedSelection.id)
    : optStr(node.props.selectedId);

  const blockColumns: ChoiceColumn[] = columns.map((column, index) => ({
    id: String(index),
    title: column.heading,
    caption: column.caption,
    highlight: selectedId !== undefined && column.items.some((item) => item.id === selectedId),
    items: column.items.map((item) => ({ ...item, disabled: item.disabled || !api?.addToMessage })),
  }));

  const handlePick = (columnId: string, itemId: string) => {
    if (!api) {
      return;
    }
    const column = columns[Number(columnId)];
    const item = column?.items.find((candidate) => candidate.id === itemId);
    if (!item || item.disabled) {
      return;
    }
    api.addToMessage?.({
      id: node.id,
      label: `${column.heading}, ${item.label}`,
      instruction: `${column.heading}, ${item.label}`,
      context: {
        component: node.component,
        nodeId: node.id,
        question: str(node.props.question ?? node.props.title),
        group: column.heading,
        selection: { ...item },
      },
    });
  };

  return (
    <div>
      <SurfaceHeader
        title={str(node.props.question ?? node.props.title)}
        subtitle={optStr(node.props.message ?? node.props.subtitle)}
      />
      <ChoiceBoardBlock columns={blockColumns} selectedId={selectedId} onPick={handlePick} />
    </div>
  );
};
